import { Controller } from "@hotwired/stimulus"
import { loadMapboxGL } from "../lib/mapbox_loader"

// Simple read-only map for displaying locations with optional polygon overlay
// Mapbox is lazy-loaded from CDN to reduce bundle size
// Usage: data-controller="static-map" data-static-map-lat-value="33.5" data-static-map-lng-value="-111.9"
export default class extends Controller {
  static values = {
    lat: Number,
    lng: Number,
    zoom: { type: Number, default: 19 },
    polygon: String,
    readonly: { type: Boolean, default: true }
  }

  async connect() {
    this.map = null
    this.mapboxgl = null
    await this.loadDependencies()
    this.initializeMap()
  }

  async loadDependencies() {
    this.mapboxgl = await loadMapboxGL()
  }

  get brandColor() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-500').trim() || '#3b82f6'
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initializeMap() {
    this.mapboxgl.accessToken = 'pk.eyJ1IjoiYWxldmVyZW56NTgiLCJhIjoiY21pZGozaTYwMDczZzJrb295bnhkY3JuOCJ9._JeXNOLxl5PhHINW4eIXUw'

    this.map = new this.mapboxgl.Map({
      container: this.element,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [this.lngValue, this.latValue],
      zoom: this.zoomValue,
      pitch: 0,
      bearing: 0,
      interactive: !this.readonlyValue,
      maxZoom: 22,
      minZoom: 16,
      antialias: true,
      fadeDuration: 0,
      preserveDrawingBuffer: true
    })

    // Add marker at center
    new this.mapboxgl.Marker({ color: this.brandColor })
      .setLngLat([this.lngValue, this.latValue])
      .addTo(this.map)

    // Add polygon overlay if provided
    this.map.on('load', () => {
      this.addPolygonOverlay()
    })
  }

  addPolygonOverlay() {
    if (!this.hasPolygonValue || !this.polygonValue) return

    try {
      const polygon = JSON.parse(this.polygonValue)
      if (!polygon || polygon.length < 3) return

      // Convert to GeoJSON coordinates [lng, lat]
      const coords = polygon.map(p => [p.lng, p.lat])
      coords.push(coords[0]) // Close the polygon

      // Add polygon source
      this.map.addSource('pool-boundary', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coords]
          }
        }
      })

      // Add fill layer
      this.map.addLayer({
        id: 'pool-boundary-fill',
        type: 'fill',
        source: 'pool-boundary',
        paint: {
          'fill-color': this.brandColor,
          'fill-opacity': 0.3
        }
      })

      // Add outline layer
      this.map.addLayer({
        id: 'pool-boundary-outline',
        type: 'line',
        source: 'pool-boundary',
        paint: {
          'line-color': this.brandColor,
          'line-width': 3
        }
      })
    } catch (error) {
      console.error('Error adding polygon overlay:', error)
    }
  }
}
